import {ChangeDetectionStrategy, Component, inject, signal} from '@angular/core';
import {FormBuilder, ReactiveFormsModule, Validators} from '@angular/forms';
import {InputTextGroupComponent} from '@brunata-angular/ui';
import {EMAIL_MAX_LENGTH, TranslatePipe, TranslateService} from '@brunata-angular/util';
import {AsyncPipe} from '@angular/common';
import {Store} from '@ngrx/store';
import {selectUser} from 'src/app/state/selectors';
import {filter, map, tap} from 'rxjs';
import {UserStateSuccess} from 'src/app/state/reducers';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {UserService, UserUpdate} from '@shared/services/user.service';
import {loadUserSuccess} from 'src/app/state/actions';
import {ToastrService} from 'ngx-toastr';
import {finalize} from 'rxjs/operators';
import {InputSelectOptionGroupComponent} from '@brunata-angular/ui';
import {environment} from 'src/environments/environment';

@Component({
    selector: 'app-my-profile',
    standalone: true,
    imports: [
        ReactiveFormsModule,
        InputTextGroupComponent,
        TranslatePipe,
        AsyncPipe,
        InputSelectOptionGroupComponent,
    ],
    templateUrl: './my-profile.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MyProfileComponent {
    private readonly store = inject(Store);
    private readonly formBuilder = inject(FormBuilder);
    private readonly toastrService = inject(ToastrService);
    private readonly userService = inject(UserService);
    private readonly translateService = inject(TranslateService);

    readonly isSaving = signal(false);

    readonly user$ = this.store.select(selectUser).pipe(
        filter((userState): userState is UserStateSuccess => userState.status === 'success'),
        map(userState => userState.user),
    );

    readonly userForm = this.formBuilder.group({
        fullName: [''],
        username: [{value: '', disabled: true}],
        email: ['', [Validators.email]],
        language: [''],
    });

    readonly languageOptions = this.getLanguageOptions();
    readonly resetPasswordUrl = this.getResetPasswordUrl();
    protected readonly emailMaxLength = EMAIL_MAX_LENGTH;

    constructor() {
        this.user$.pipe(takeUntilDestroyed()).subscribe(user => {
            this.userForm.patchValue(user);
            if (user.userType === 'CUSTOMER') {
                this.userForm.controls.fullName.disable();
            } else {
                this.userForm.controls.fullName.setValidators(Validators.required);
            }
            if (user.email != null) {
                this.userForm.controls.email.setValidators(Validators.required);
            }
        });
    }

    onSubmit(): void {
        this.userForm.markAsTouched();
        if (!this.userForm.valid) {
            return;
        }

        this.isSaving.set(true);
        const userUpdate: UserUpdate = {
            email: this.userForm.value.email ?? '',
            language: this.userForm.value.language ?? null,
        };
        if (this.userForm.value.fullName != null) {
            userUpdate.personName = this.userForm.value.fullName;
        }
        this.userService
            .updateUser(userUpdate)
            .pipe(
                tap(user => this.store.dispatch(loadUserSuccess({user}))),
                tap(() => {
                    this.toastrService.success(
                        '',
                        this.translateService.translate(
                            'alerts_userProfileUpdateSuccess',
                            'online',
                            'common',
                        ),
                    );
                }),
                finalize(() => this.isSaving.set(false)),
            )
            .subscribe();
    }

    private getLanguageOptions(): Map<string, string> {
        return new Map(
            this.translateService
                .getSupportedLanguages()
                .map(language => [
                    language,
                    this.translateService.translate(
                        `mappers_language.${language}`,
                        'online',
                        'common',
                    ),
                ]),
        );
    }

    private getResetPasswordUrl(): string {
        const clientId = environment.oauthClientId;
        return `${environment.keycloakResetPasswordUrl}?client_id=${clientId}`;
    }
}
